// useSites — PowerSync-backed hook for reading the site list scoped to
// a specific base. Replaces the REST-backed `useCaptureSites` hook for
// Phase D-2 of the PowerSync rollout.
//
// Query shape mirrors the contract expected by `BaseDetailPage`:
//   - id, base_id, name, code, display_order from the `sites` table
//   - building_count derived via a correlated sub-query against `buildings`
//
// The query is reactive: whenever the `sites` or `buildings` table
// changes in local SQLite (e.g. after a PowerSync sync cycle delivers
// new rows), the hook re-renders automatically with no manual
// invalidation.
//
// The `base` summary row is fetched separately so the breadcrumb can
// render the actual base name. In the REST path this came from the
// GET /bases/:id/sites response envelope. In the PowerSync path we
// query the `bases` table directly — same local SQLite, same reactive
// behaviour.
//
// Ordering follows buildspec §5.4a: display_order ASC, then name ASC.
// The ORDER BY clause in the SQL carries that contract so the UI
// renders in the correct order even if rows arrive out-of-sequence from
// the sync stream.
//
// Column-name note: the PowerSync client schema uses snake_case to
// mirror the Postgres column names exactly. The returned rows use
// snake_case; callers map to camelCase as needed (or use the type-safe
// `UseSitesRow` / `UseSitesBase` types exported below).
//
// Phase D-2 constraint: `buildingCount` is derived by sub-querying
// the local `buildings` table. This means the count reflects locally
// synced data only — in Phase D-2 this is acceptable because:
//   1. The user's assigned buildings are all synced by the
//      `reference_site_scoped` stream before the site list renders.
//   2. Any newly added buildings (field additions) flow through the
//      same sync stream before appearing in the count.
// If an admin needs counts that include buildings not yet synced to
// this device, those will only be visible after a full sync cycle.
// This is an inherent property of the offline-first model and
// matches the user's mental model: "what I can see offline is what
// I have synced."
//
// See: docs/plans/powersync-implementation.md Phase D section.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import { db } from './client'
import type { SiteRecord } from './schema'

// Row type returned by the hook — mirrors CaptureSiteRow in
// `apps/frontend/src/capture/hooks/use-capture-sites.ts` so the
// consuming page (BaseDetailPage) can swap the hook with minimal
// churn. Uses snake_case because that is what PowerSync returns;
// the page converts to presentation labels directly.
export interface UseSitesRow extends SiteRecord {
  /** id is auto-created by PowerSync — always present at runtime */
  id: string
  /** Number of buildings under this site in the local SQLite mirror */
  building_count: number
}

// Base summary row — minimum needed for the breadcrumb in
// BaseDetailPage. Sourced from the `bases` table in the local mirror.
export interface UseSitesBase {
  id: string
  name: string
}

export interface UseSitesResult {
  sites: UseSitesRow[]
  base: UseSitesBase | null
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

/**
 * Returns the site list for a given base, backed by PowerSync local
 * SQLite. Results are reactive — any sync-delivered change to `sites`
 * or `buildings` triggers an automatic re-render.
 *
 * @param baseId UUID of the base whose sites to list.
 */
export function useSites(baseId: string): UseSitesResult {
  const sitesResult = useQuery<UseSitesRow>(
    `SELECT
       s.id,
       s.base_id,
       s.name,
       s.code,
       s.display_order,
       s.created_at,
       s.updated_at,
       (SELECT COUNT(*) FROM buildings b WHERE b.site_id = s.id
         -- Keep the count consistent with the field building list:
         -- admin-only statuses (to_be_demolished / out_of_scope) are
         -- hidden from field staff (see use-buildings.ts).
         AND b.status NOT IN ('to_be_demolished', 'out_of_scope')) AS building_count
     FROM sites s
     WHERE s.base_id = ?
     ORDER BY s.display_order ASC, s.name ASC`,
    [baseId],
  )

  const basesResult = useQuery<UseSitesBase>(
    `SELECT id, name FROM bases WHERE id = ? LIMIT 1`,
    [baseId],
  )

  // PowerSync stream-readiness — gates the empty state on the
  // SPECIFIC stream this screen depends on (`reference_site_scoped`),
  // NOT on `useStatus().hasSynced` which is the global all-streams
  // signal. The global signal is too broad for this screen's contract:
  // we auto-subscribe to four streams (reference_global,
  // reference_site_scoped, assignments, captures), and `captures` is
  // estimated at 500–25 000 rows. A user with zero sites would
  // otherwise spin until their unrelated capture history finished
  // syncing, which violates the Phase D requirement that the migrated
  // screen keep working offline / on slow startups.
  //
  // `useAllSyncStreamsHaveSynced(db, [...])` returns true once every
  // stream in the array has its `subscription.hasSynced === true`.
  // We pass `waitForStream: true` because the function only blocks
  // on streams that explicitly opt in to the wait. Returns true
  // immediately if the array is empty.
  const referenceSiteScopedSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_site_scoped', waitForStream: true },
  ])

  // Stale-data guard against base-route navigation:
  //
  // @powersync/react's `useQuery` keeps the previous result's `data`
  // visible across parameter changes — it only flips `isFetching` to
  // signal the transition. `isLoading` stays false. Without an
  // explicit check, navigating /capture/bases/A → /capture/bases/B
  // would briefly render A's sites + A's base name under B's URL —
  // a contractually significant accuracy failure given asset counts
  // are scoped per base/site (the contract baseline data — see
  // AGENTS.md core principle). Each returned row's `base_id` (or
  // for the base row,
  // `id`) must match the currently requested `baseId`; if not, the
  // data is from the prior route and we treat it as still loading.
  //
  // Empty arrays are not considered stale — they're either a true
  // empty state OR the initial "no sync yet" state. The
  // initial-sync-gating concern (rendering "no sites" before the
  // reference_site_scoped stream has populated local SQLite for the
  // first time) is tracked as a follow-up in
  // docs/plans/powersync-implementation.md Phase D — see TODO below.
  const sitesAreForBaseId =
    sitesResult.data.length === 0 ||
    sitesResult.data.every((s) => s.base_id === baseId)
  const baseIsForBaseId =
    basesResult.data.length === 0 ||
    (basesResult.data[0]?.id ?? '') === baseId
  const isStale = !sitesAreForBaseId || !baseIsForBaseId

  const base =
    basesResult.data.length > 0 && baseIsForBaseId
      ? (basesResult.data[0] ?? null)
      : null

  // Initial-sync gating (scoped): when the `reference_site_scoped`
  // stream hasn't synced yet, an empty sitesResult is NOT
  // authoritative — it just means the stream hasn't delivered rows.
  // Without this gate, a freshly-installed device would flash
  // "No sites yet in this base" before the stream's first sync
  // completes, on the primary field workflow.
  //
  // Bases and sites are delivered by SEPARATE queries within the
  // `reference_site_scoped` stream — the base row can land before
  // the sites rows. Without this gate we'd render "no sites" the
  // instant the base row arrived, even though the sites are still
  // in flight.
  //
  // Once the stream has its first sync, an empty sites array IS
  // authoritative — the user truly has no sites for this base.
  const sitesAreEmptyButUnsynced =
    sitesResult.data.length === 0 && !referenceSiteScopedSynced

  // Combine loading/error state from both queries. The screen is
  // "loading" until both queries have resolved their initial result
  // AND the resolved data matches the currently requested baseId
  // AND PowerSync has completed at least one full sync (so empty
  // results are authoritative).
  const isLoading =
    sitesResult.isLoading ||
    basesResult.isLoading ||
    isStale ||
    sitesAreEmptyButUnsynced
  const isFetching = sitesResult.isFetching || basesResult.isFetching
  const error = sitesResult.error ?? basesResult.error

  return {
    sites: isStale ? [] : sitesResult.data,
    base,
    isLoading,
    isFetching,
    error,
  }
}
